import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { GbBaseApiService } from 'libs/shared/data-access-core/src/lib/+state/gb-base-api.service'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { Observable } from 'rxjs'
import {
  AssignEventTicketPayload,
  BulkAssignEventTicketsResponse,
  EditEventTicketPayload,
  EventGuestSummary,
  EventTicketCheckInResponse,
  EventTicketFilters,
  EventTicketResponse,
  TransferEventTicketPayload,
  TransferEventTicketResponse,
} from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { GbPaginatedResponse } from 'libs/shared/models/shared.models'
import { BaseEventTicketEntity } from './base-event-tickets.models'
import { Params } from '@angular/router'

@Injectable({
  providedIn: 'root',
})
export class BaseEventTicketsApiService extends GbBaseApiService {
  constructor(protected http: HttpClient, @Inject(APP_CONFIG) protected appConfig: any) {
    super(http, appConfig)
  }

  getEventTickets(
    urlName = '',
    limit?: number,
    offset?: number,
    filters?: EventTicketFilters,
  ): Observable<GbPaginatedResponse<BaseEventTicketEntity>> {
    let paramsObject: Params = { limit, offset, ...filters }

    // remove undefined values from the params object
    paramsObject = Object.entries(paramsObject).reduce((acc, [key, value]) => {
      if (value !== undefined) acc[key] = value
      return acc
    }, {} as Params)

    const params = new HttpParams({ fromObject: paramsObject })

    return this.get<GbPaginatedResponse<BaseEventTicketEntity>>(`auctions/${urlName}/event-tickets/`, { params })
  }

  getEventTicket(urlName = '', eventTicketId: string, token?: string): Observable<BaseEventTicketEntity> {
    let url = `auctions/${urlName}/event-tickets/${eventTicketId}/`
    if (token) url += `?invite_token=${token}`

    return this.get<BaseEventTicketEntity>(url)
  }

  getEventGuestSummary(urlName = ''): Observable<EventGuestSummary> {
    return this.get<EventGuestSummary>(`auctions/${urlName}/event-guest-summary/`)
  }

  assignEventTicket(
    urlName = '',
    payload: AssignEventTicketPayload,
    eventTicketId = '',
  ): Observable<EventTicketResponse> {
    return this.post<EventTicketResponse>(`auctions/${urlName}/event-tickets/${eventTicketId}/invite/`, payload)
  }

  bulkAssignEventTickets(
    urlName = '',
    payload: AssignEventTicketPayload[],
  ): Observable<BulkAssignEventTicketsResponse> {
    return this.post<BulkAssignEventTicketsResponse>(`auctions/${urlName}/event-tickets/bulk-invite/`, {
      invites: payload,
    })
  }

  editEventTicket(urlName = '', payload: EditEventTicketPayload, eventTicketId = ''): Observable<EventTicketResponse> {
    return this.post<EventTicketResponse>(
      `auctions/${urlName}/event-tickets/${eventTicketId}/update-guest-details/`,
      payload,
    )
  }

  /**
   * Sends a POST request which either resends invite or the ticket.
   * The API determines the appropriate action based on the ticket status.
   */
  sendEventTicket(urlName = '', eventTicketId = ''): Observable<{}> {
    return this.post<{}>(`auctions/${urlName}/event-tickets/${eventTicketId}/send-ticket/`)
  }

  transferEventTicket(
    urlName = '',
    payload: TransferEventTicketPayload,
    eventTicketId = '',
  ): Observable<TransferEventTicketResponse> {
    return this.post<TransferEventTicketResponse>(
      `auctions/${urlName}/event-tickets/${eventTicketId}/transfer/`,
      payload,
    )
  }

  checkIn(urlName = '', eventTicketId: string): Observable<EventTicketCheckInResponse> {
    return this.post<EventTicketCheckInResponse>(`auctions/${urlName}/event-tickets/${eventTicketId}/check-in/`)
  }

  checkOut(urlName = '', eventTicketId: string): Observable<any> {
    return this.post<any>(`auctions/${urlName}/event-tickets/${eventTicketId}/check-out/`)
  }
}
